import React from 'react';
import { TableBlock } from '../../ast/document.types';

interface TableBlockRendererProps {
  block: TableBlock;
}

export const TableBlockRenderer: React.FC<TableBlockRendererProps> = ({ block }) => {
  const { cells, hasHeaderRow, caption, borderStyle = 'academic', alignment = 'center', offsetX = 0, offsetY = 0 } = block.data;

  // Academic style (APA standard): top border, bottom of header border, bottom of table border
  const getTableStyle = (): React.CSSProperties => {
    switch (borderStyle) {
      case 'grid':
        return { borderCollapse: 'collapse', width: '100%', border: '1px solid #111827' };
      case 'academic':
        return { borderCollapse: 'collapse', width: '100%', borderTop: '2px solid #111827', borderBottom: '2px solid #111827' };
      case 'minimal':
        return { borderCollapse: 'collapse', width: '100%', borderBottom: '1px solid #e5e7eb' };
      default:
        return { borderCollapse: 'collapse', width: '100%' };
    }
  };

  const getCellStyle = (isHeaderRow: boolean): React.CSSProperties => {
    const base: React.CSSProperties = {
      padding: '6px 12px',
      fontSize: '12px',
      color: '#111827',
    };

    if (borderStyle === 'grid') {
      return { ...base, border: '1px solid #d1d5db', backgroundColor: isHeaderRow ? '#f9fafb' : undefined };
    }

    if (borderStyle === 'academic') {
      if (isHeaderRow) {
        return { ...base, borderBottom: '1.5px solid #111827', fontWeight: 700, backgroundColor: '#f9fafb' };
      }
      return { ...base, borderBottom: '1px solid #f3f4f6' };
    }

    if (borderStyle === 'minimal') {
      return { ...base, borderBottom: '1px solid #e5e7eb', fontWeight: isHeaderRow ? 600 : undefined };
    }

    return base;
  };

  return (
    <div
      style={{
        margin: '14px 0',
        width: '100%',
        overflowX: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: alignment === 'left' ? 'flex-start' : alignment === 'right' ? 'flex-end' : 'center',
        transform: `translate(${offsetX}px, ${offsetY}px)`,
        transition: 'transform 0.05s ease-out',
      }}
    >
      {caption && (
        <div style={{ fontSize: '11.5px', fontWeight: 600, color: '#374151', marginBottom: '6px', textAlign: 'left' }}>
          {caption}
        </div>
      )}

      <table style={getTableStyle()}>
        <tbody>
          {cells.map((row, rIdx) => {
            const isHeaderRow = hasHeaderRow && rIdx === 0;
            return (
              <tr key={rIdx}>
                {row.map((cell, cIdx) => (
                  <td
                    key={cell.id || cIdx}
                    style={{
                      ...getCellStyle(isHeaderRow),
                      textAlign: cell.alignment || (isHeaderRow ? 'center' : 'left'),
                      fontWeight: cell.bold || isHeaderRow ? 700 : 'normal',
                    }}
                  >
                    {cell.content || '—'}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
